import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, BASE, getAuthHeaders } from '@/api/client'
import type { AiLog, AiLogSegment, AiLogReason } from '@/types'

export type { AiLog, AiLogSegment, AiLogReason }

interface VideoCutParams {
  videoUrl: string
  maxClips: number
  maxDuration: number
  folderId: string
  clipTitle: string
  reelsMode?: boolean
  nsfwReview?: boolean
  outputQuality?: '720p' | '1080p'
  aiSensitivity?: 'low' | 'medium' | 'high'
  blurIntensity?: 'light' | 'medium' | 'strong'
  clipModel?: 'openclip' | 'dinov2'
}

interface ClipResult {
  id: string
  url: string
  filename: string
  duration: number
}

interface VideoCutResponse {
  ok: boolean
  clips: ClipResult[]
  totalDuration: number
  targetFolderId: string
  targetFolderName: string
}

export function useVideoCut() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: VideoCutParams) =>
      api.post<VideoCutResponse>('/video/cut', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-folders'] }),
  })
}

export interface CutSSEEvent {
  type: 'stage' | 'start' | 'clip-progress' | 'clip-done' | 'done' | 'error'
  data: {
    stage?: string
    message?: string
    clip?: number
    total?: number
    totalClips?: number
    totalDuration?: number
    nsfwDetected?: boolean
    filename?: string
    url?: string
    segments?: number
    error?: string
    aiLog?: AiLog
    // done payload
    ok?: boolean
    clips?: ClipResult[]
    targetFolderId?: string
    targetFolderName?: string
  }
}

/**
 * Cut a video with NSFW review via SSE for real-time progress.
 * Returns a promise that resolves with the final VideoCutResponse.
 */
export async function cutWithNsfwSSE(
  params: VideoCutParams,
  onEvent: (event: CutSSEEvent) => void,
): Promise<VideoCutResponse> {
  const resp = await fetch(`${BASE}/video/cut`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(params),
  })

  if (!resp.ok || !resp.body) {
    const body = await resp.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(body.error || `HTTP ${resp.status}`)
  }

  const reader = resp.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let finalResult: VideoCutResponse | null = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    let eventType = ''
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7)
      } else if (line.startsWith('data: ') && eventType) {
        try {
          const data = JSON.parse(line.slice(6))
          onEvent({ type: eventType as CutSSEEvent['type'], data })

          if (eventType === 'done') {
            finalResult = data as VideoCutResponse
          }
          if (eventType === 'error') {
            throw new Error(data.error || 'Erro no processamento')
          }
        } catch (e) {
          if (e instanceof Error && e.message !== 'Unexpected end of JSON input') {
            throw e
          }
        }
        eventType = ''
      }
    }
  }

  if (!finalResult) {
    throw new Error('Processamento interrompido sem resultado')
  }

  return finalResult
}
